import z from "zod/v4";
import { TenantId, SiteId, AssetId, CameraId, AssetCameraKey } from ".";

/** Wraps a decode function to catch errors and add Zod issues instead of throwing */
function safeDecode<T>(decode: (s: string) => T, fallbackMessage: string) {
  return (s: string, ctx: z.core.ParsePayload<string>) => {
    try {
      return decode(s);
    } catch (e) {
      ctx.issues.push({ code: "custom", input: s, message: e instanceof Error ? e.message : fallbackMessage });
      return z.NEVER;
    }
  };
}

/**
 * Codec for TenantId: string ↔ TenantId branded type
 */
export const TenantIdCodec = z.codec(z.string(), z.custom<TenantId>(), {
  decode: safeDecode(TenantId.fromString, "Invalid TenantId"),
  encode: TenantId.toString,
});

/**
 * Codec for SiteId: string ↔ SiteId branded type
 */
export const SiteIdCodec = z.codec(z.string(), z.custom<SiteId>(), {
  decode: safeDecode(SiteId.fromString, "Invalid SiteId"),
  encode: SiteId.toString,
});

/**
 * Codec for AssetId: string ↔ AssetId branded type
 */
export const AssetIdCodec = z.codec(z.string(), z.custom<AssetId>(), {
  decode: safeDecode(AssetId.fromString, "Invalid AssetId"),
  encode: AssetId.toString,
});

/**
 * Codec for CameraId: string ↔ CameraId branded type
 */
export const CameraIdCodec = z.codec(z.string(), z.custom<CameraId>(), {
  decode: safeDecode(CameraId.fromString, "Invalid CameraId"),
  encode: CameraId.toString,
});

export const AssetCameraKeySchema = z.codec(z.string(), z.custom<AssetCameraKey>(), {
  decode: safeDecode(AssetCameraKey.fromString, "Invalid AssetCameraKey"),
  encode: AssetCameraKey.toString,
});

/**
 * Schema for InstalledAsset object (tenant + site + asset)
 */
export const InstalledAssetSchema = z.object({
  tenant: TenantIdCodec,
  site: SiteIdCodec,
  asset: AssetIdCodec,
});

/**
 * Schema for InstalledCamera object (tenant + site + asset + camera)
 */
export const InstalledCameraSchema = z.object({
  tenant: TenantIdCodec,
  site: SiteIdCodec,
  asset: AssetIdCodec,
  camera: CameraIdCodec,
});

// Re-export InstalledAsset input codecs
export {
  InstalledAssetInput,
  InstalledAssetInputTopic,
  InstalledAssetInputUrl,
  InstalledAssetInputTuple,
} from "./installed-asset-input";

// Re-export InstalledCamera input codecs
export {
  InstalledCameraInput,
  InstalledCameraInputTopic,
  InstalledCameraInputUrl,
  InstalledCameraInputTuple,
} from "./installed-camera-input";
